import React, { useState } from "react";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "../hooks/useAuth";

type Category = "bug" | "suggestion" | "question" | "other";

const CATEGORIES: { value: Category; label: string; emoji: string }[] = [
  { value: "bug", label: "Bug", emoji: "🐛" },
  { value: "suggestion", label: "Suggestion", emoji: "💡" },
  { value: "question", label: "Question", emoji: "❓" },
  { value: "other", label: "Other", emoji: "💬" },
];

// Extract the property UUID from ?property=<id> if we're on a property
// URL. Helps the admin triage "bug on property X" without asking users
// which property they were viewing.
function capturePropertyId(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("property");
    // Quick sanity check — only pass through if it looks like a UUID.
    if (id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      return id;
    }
  } catch {
    // SSR / edge cases — just skip
  }
  return undefined;
}

const FeedbackButton: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  // Email is no longer typed — we attach the signed-in account's email
  // automatically. Phone is an optional callback number.
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState<Category>("suggestion");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    setError(null);
    try {
      await apiClient.post("/feedback", {
        message,
        email: user?.email || undefined,
        phone: phone.trim() || undefined,
        category,
        pageUrl: window.location.pathname + window.location.search,
        propertyId: capturePropertyId(),
      });
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
        setMessage("");
        setPhone("");
        setCategory("suggestion");
      }, 2000);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to send — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        className="feedback-btn"
        data-tour="feedback"
        onClick={() => setOpen(!open)}
        aria-label="Send feedback"
        title="Send feedback"
        aria-expanded={open}
      >
        <span aria-hidden="true">💬</span>
      </button>
      {open && (
        <div className="feedback-modal" role="dialog" aria-label="Send feedback">
          {sent ? (
            <div className="feedback-success">Thanks for your feedback!</div>
          ) : (
            <>
              <h3>Send Feedback</h3>
              <div className="feedback-category-row">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={`feedback-category${category === c.value ? " feedback-category--active" : ""}`}
                    onClick={() => setCategory(c.value)}
                    aria-pressed={category === c.value}
                  >
                    <span>{c.emoji}</span>
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
              <textarea
                aria-label="Your feedback"
                placeholder={
                  category === "bug"
                    ? "What's broken? Include the steps to reproduce if you can."
                    : category === "suggestion"
                    ? "What would make this better?"
                    : category === "question"
                    ? "Ask us anything."
                    : "What's on your mind?"
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
              />
              <input
                type="tel"
                aria-label="Phone number (optional)"
                placeholder="Phone (optional — if you'd like a call back)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: 8,
                  padding: "8px 10px",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                  background: "var(--bg-input)",
                  color: "var(--text-primary)",
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-sm)",
                }}
              />
              {error && <div className="feedback-error">{error}</div>}
              <div className="feedback-modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setOpen(false)}
                  style={{ padding: "6px 12px", fontSize: "var(--text-sm)" }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSubmit}
                  disabled={sending || !message.trim()}
                  style={{ padding: "6px 12px", fontSize: "var(--text-sm)" }}
                >
                  {sending ? "..." : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
